import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Save,
  RotateCcw,
  Image,
  Type,
  LayoutGrid,
  Trophy,
  Target,
  MessageSquare,
  MapPin,
  ChevronDown,
  ChevronRight,
  Upload,
  Loader2,
  Eye,
  EyeOff,
  Layers,
  Shield,
  GripVertical,
  Search,
  Video,
  Plus,
  Trash2,
  ClipboardList,
  Link2,
  Award,
  HelpCircle,
  Edit,
  ExternalLink,
  CalendarDays,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableSectionItem = ({ section, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded border ${
        isDragging ? "shadow-lg ring-2 ring-cfc-accent" : ""
      } ${section.enabled ? "border-cfc-border bg-white" : "border-dashed border-gray-300 bg-gray-50"}`}
      data-testid={`section-row-${section.key}`}
    >
      <button
        className="cursor-grab active:cursor-grabbing text-cfc-text-muted hover:text-cfc-primary touch-none"
        {...attributes}
        {...listeners}
        data-testid={`drag-handle-${section.key}`}
      >
        <GripVertical size={18} />
      </button>
      <span className={`flex-1 text-sm font-medium ${section.enabled ? "text-cfc-primary" : "text-cfc-text-muted line-through"}`}>
        {section.label}
      </span>
      <Switch
        checked={section.enabled}
        onCheckedChange={() => onToggle(section.key)}
        data-testid={`toggle-${section.key}`}
      />
      {section.enabled ? <Eye size={14} className="text-green-600" /> : <EyeOff size={14} className="text-gray-400" />}
    </div>
  );
};

const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="border-cfc-border">
      <button
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-cfc-cream-alt transition-colors"
        onClick={() => setOpen(!open)}
        data-testid={`section-toggle-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon size={20} className="text-cfc-accent flex-shrink-0" />
        <span className="font-heading text-lg font-bold text-cfc-primary flex-1">{title}</span>
        {open ? <ChevronDown size={18} className="text-cfc-text-muted" /> : <ChevronRight size={18} className="text-cfc-text-muted" />}
      </button>
      {open && <CardContent className="pt-0 pb-4 px-4 space-y-4">{children}</CardContent>}
    </Card>
  );
};

const FieldGroup = ({ label, children }) => (
  <div>
    <Label className="text-xs text-cfc-text-muted uppercase tracking-wider mb-1 block">{label}</Label>
    {children}
  </div>
);

const ImageFitSelect = ({ value, onChange, testId }) => (
  <FieldGroup label="Image Fit">
    <Select value={value || "cover"} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="cover">Cover (fill & crop)</SelectItem>
        <SelectItem value="contain">Contain (show full image)</SelectItem>
        <SelectItem value="auto">Auto (natural size)</SelectItem>
        <SelectItem value="fill">Stretch to fill</SelectItem>
      </SelectContent>
    </Select>
  </FieldGroup>
);

const ImageUploadField = ({ label, value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const imageUrl = `${API}/files/${res.data.file_id}`;
      onChange(imageUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed — check storage config");
    } finally {
      setUploading(false);
    }
  };

  return (
    <FieldGroup label={label}>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL or upload..."
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-10 px-3 relative"
          disabled={uploading}
          onClick={() => document.getElementById(`upload-${label.replace(/\s/g, '')}`)?.click()}
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        </Button>
        <input
          id={`upload-${label.replace(/\s/g, '')}`}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUpload}
        />
      </div>
      {value && (
        <img
          src={value}
          alt="Preview"
          className="mt-2 h-32 object-cover rounded border border-cfc-border"
          onError={(e) => { e.target.style.display = "none"; }}
          onLoad={(e) => { e.target.style.display = "block"; }}
        />
      )}
    </FieldGroup>
  );
};

// ─── QUICK LINKS (inline manager) ───────────────────────────────
const QuickLinksSection = () => {
  const [links, setLinks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", url: "", is_active: true, order: 0 });

  const fetch_ = async () => { try { const r = await axios.get(`${API}/quick-links`, { withCredentials: true }); setLinks(r.data); } catch (e) { console.error("Quick links fetch failed:", e); } };
  useEffect(() => { fetch_(); }, []);

  const open = (link = null) => {
    if (link) { setEditing(link); setForm({ label: link.label, url: link.url, is_active: link.is_active, order: link.order || 0 }); }
    else { setEditing(null); setForm({ label: "", url: "", is_active: true, order: links.length }); }
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.label.trim() || !form.url.trim()) { toast.error("Label and URL required"); return; }
    try {
      if (editing) await axios.put(`${API}/quick-links/${editing.link_id}`, form, { withCredentials: true });
      else await axios.post(`${API}/quick-links`, form, { withCredentials: true });
      toast.success(editing ? "Link updated" : "Link created");
      setDialogOpen(false); fetch_();
    } catch { toast.error("Failed to save"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this link?")) return; try { await axios.delete(`${API}/quick-links/${id}`, { withCredentials: true }); toast.success("Deleted"); fetch_(); } catch { toast.error("Failed"); } };
  const toggle = async (link) => { try { await axios.put(`${API}/quick-links/${link.link_id}`, { is_active: !link.is_active }, { withCredentials: true }); fetch_(); } catch { toast.error("Failed"); } };

  return (
    <Section title="Quick Links" icon={Link2}>
      <p className="text-xs text-cfc-text-muted mb-3">Homepage announcement links. Drag to reorder.</p>
      <div className="space-y-2 mb-3">
        {links.map((link) => (
          <div key={link.link_id} className="flex items-center gap-3 p-3 bg-white border border-cfc-border rounded-sm" data-testid={`quick-link-${link.link_id}`}>
            <GripVertical size={14} className="text-cfc-text-muted flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className={`text-sm font-medium ${link.is_active ? "text-cfc-primary" : "text-cfc-text-muted line-through"}`}>{link.label}</span>
              <span className="text-xs text-cfc-text-muted block truncate">{link.url}</span>
            </div>
            <Switch checked={link.is_active} onCheckedChange={() => toggle(link)} />
            <Button variant="ghost" size="sm" onClick={() => open(link)}><Edit size={14} /></Button>
            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => del(link.link_id)}><Trash2 size={14} /></Button>
          </div>
        ))}
        {links.length === 0 && <p className="text-sm text-cfc-text-muted text-center py-4">No quick links yet</p>}
      </div>
      <Button variant="outline" size="sm" onClick={() => open()} data-testid="add-quick-link-btn"><Plus size={14} className="mr-1" /> Add Link</Button>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="quick-link-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg text-cfc-primary">{editing ? "Edit Link" : "New Link"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Spring Registration Open" data-testid="quick-link-label-input" /></div>
            <div><Label>URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="quick-link-url-input" /></div>
            <div><Label>Display Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={save} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-quick-link-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
};

// ─── SPONSORS (inline manager) ──────────────────────────────────
const SponsorsSection = () => {
  const [sponsors, setSponsors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", logo_url: "", website_url: "", is_active: true, order: 0 });

  const fetch_ = async () => { try { const r = await axios.get(`${API}/sponsors`, { withCredentials: true }); setSponsors(r.data); } catch (e) { console.error("Sponsors fetch failed:", e); } };
  useEffect(() => { fetch_(); }, []);

  const open = (sp = null) => {
    if (sp) { setEditing(sp); setForm({ name: sp.name, logo_url: sp.logo_url, website_url: sp.website_url || "", is_active: sp.is_active, order: sp.order || 0 }); }
    else { setEditing(null); setForm({ name: "", logo_url: "", website_url: "", is_active: true, order: sponsors.length }); }
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.name.trim() || !form.logo_url.trim()) { toast.error("Name and Logo URL required"); return; }
    try {
      if (editing) await axios.put(`${API}/sponsors/${editing.sponsor_id}`, form, { withCredentials: true });
      else await axios.post(`${API}/sponsors`, form, { withCredentials: true });
      toast.success(editing ? "Updated" : "Added");
      setDialogOpen(false); fetch_();
    } catch { toast.error("Failed to save"); }
  };
  const del = async (id) => { if (!window.confirm("Remove this sponsor?")) return; try { await axios.delete(`${API}/sponsors/${id}`, { withCredentials: true }); toast.success("Removed"); fetch_(); } catch { toast.error("Failed"); } };

  return (
    <Section title="Sponsors & Partners" icon={Award}>
      <p className="text-xs text-cfc-text-muted mb-3">Logos displayed on the homepage sponsor bar.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
        {sponsors.map((sp) => (
          <div key={sp.sponsor_id} className="bg-white border border-cfc-border rounded-sm p-3" data-testid={`sponsor-${sp.sponsor_id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-medium ${sp.is_active ? "text-cfc-primary" : "text-cfc-text-muted"}`}>{sp.name}</span>
              <div className="flex gap-0.5">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => open(sp)}><Edit size={12} /></Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => del(sp.sponsor_id)}><Trash2 size={12} /></Button>
              </div>
            </div>
            <div className="bg-cfc-cream rounded flex items-center justify-center h-14">
              {sp.logo_url ? <img src={sp.logo_url} alt={sp.name} className="max-h-10 max-w-full object-contain" /> : <Image size={18} className="text-cfc-text-muted" />}
            </div>
            {sp.website_url && <a href={sp.website_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-cfc-text-muted hover:text-cfc-accent flex items-center gap-1 mt-1 truncate"><ExternalLink size={9} />{sp.website_url}</a>}
          </div>
        ))}
      </div>
      {sponsors.length === 0 && <p className="text-sm text-cfc-text-muted text-center py-4 mb-3">No sponsors yet</p>}
      <Button variant="outline" size="sm" onClick={() => open()} data-testid="add-sponsor-btn"><Plus size={14} className="mr-1" /> Add Sponsor</Button>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="sponsor-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg text-cfc-primary">{editing ? "Edit Sponsor" : "Add Sponsor"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Nike" data-testid="sponsor-name-input" /></div>
            <div>
              <Label>Logo URL</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." data-testid="sponsor-logo-input" />
              {form.logo_url && <div className="mt-2 bg-white border rounded p-2 flex items-center justify-center"><img src={form.logo_url} alt="Preview" className="max-h-10 object-contain" onError={(e) => { e.target.style.display = "none"; }} /></div>}
            </div>
            <div><Label>Website (optional)</Label><Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Display Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={save} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-sponsor-btn">{editing ? "Update" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
};

// ─── FAQ MANAGER (inline) ───────────────────────────────────────
const SortableFAQItem = ({ faq, onEdit, onDelete, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: faq.faq_id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : 0, opacity: isDragging ? 0.8 : 1 };
  return (
    <div ref={setNodeRef} style={style}>
      <div className={`flex items-start gap-2 p-3 bg-white border border-cfc-border rounded-sm ${isDragging ? "shadow-lg ring-2 ring-cfc-accent" : ""}`} data-testid={`faq-admin-${faq.faq_id}`}>
        <button className="cursor-grab active:cursor-grabbing text-cfc-text-muted hover:text-cfc-primary mt-0.5 touch-none" {...attributes} {...listeners}><GripVertical size={14} /></button>
        <div className="flex-1 min-w-0">
          <span className={`text-sm font-medium ${faq.is_active ? "text-cfc-primary" : "text-cfc-text-muted line-through"}`}>{faq.question}</span>
          {faq.question_es && <span className="ml-2 text-[10px] bg-blue-50 text-blue-600 px-1 py-0.5 rounded">ES</span>}
          <p className="text-xs text-cfc-text-muted line-clamp-1 mt-0.5">{faq.answer}</p>
        </div>
        <Switch checked={faq.is_active} onCheckedChange={() => onToggle(faq)} />
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(faq)}><Edit size={13} /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => onDelete(faq.faq_id)}><Trash2 size={13} /></Button>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const [faqs, setFaqs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ question: "", answer: "", question_es: "", answer_es: "", is_active: true, order: 0 });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const fetch_ = async () => { try { const r = await axios.get(`${API}/faq`, { withCredentials: true }); setFaqs(r.data); } catch (e) { console.error("FAQ fetch failed:", e); } };
  useEffect(() => { fetch_(); }, []);

  const open = (faq = null) => {
    if (faq) { setEditing(faq); setForm({ question: faq.question, answer: faq.answer, question_es: faq.question_es || "", answer_es: faq.answer_es || "", is_active: faq.is_active, order: faq.order || 0 }); }
    else { setEditing(null); setForm({ question: "", answer: "", question_es: "", answer_es: "", is_active: true, order: faqs.length }); }
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) { toast.error("Question and answer required"); return; }
    try {
      if (editing) await axios.put(`${API}/faq/${editing.faq_id}`, form, { withCredentials: true });
      else await axios.post(`${API}/faq`, form, { withCredentials: true });
      toast.success(editing ? "Updated" : "Created");
      setDialogOpen(false); fetch_();
    } catch { toast.error("Failed to save"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this FAQ?")) return; try { await axios.delete(`${API}/faq/${id}`, { withCredentials: true }); toast.success("Deleted"); fetch_(); } catch { toast.error("Failed"); } };
  const toggle = async (faq) => { try { await axios.put(`${API}/faq/${faq.faq_id}`, { is_active: !faq.is_active }, { withCredentials: true }); fetch_(); } catch { toast.error("Failed"); } };
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const sorted = [...faqs].sort((a, b) => a.order - b.order);
    const oldIdx = sorted.findIndex((f) => f.faq_id === active.id);
    const newIdx = sorted.findIndex((f) => f.faq_id === over.id);
    const reordered = arrayMove(sorted, oldIdx, newIdx);
    setFaqs(reordered.map((f, i) => ({ ...f, order: i })));
    for (let i = 0; i < reordered.length; i++) {
      await axios.put(`${API}/faq/${reordered[i].faq_id}`, { order: i }, { withCredentials: true }).catch(() => {});
    }
  };
  const sorted = [...faqs].sort((a, b) => a.order - b.order);

  return (
    <Section title="FAQ Manager" icon={HelpCircle}>
      <p className="text-xs text-cfc-text-muted mb-3">Drag to reorder. Supports English + Spanish translations.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sorted.map((f) => f.faq_id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2 mb-3">
            {sorted.map((faq) => <SortableFAQItem key={faq.faq_id} faq={faq} onEdit={open} onDelete={del} onToggle={toggle} />)}
          </div>
        </SortableContext>
      </DndContext>
      {faqs.length === 0 && <p className="text-sm text-cfc-text-muted text-center py-4 mb-3">No FAQ items yet</p>}
      <Button variant="outline" size="sm" onClick={() => open()} data-testid="add-faq-btn"><Plus size={14} className="mr-1" /> Add FAQ</Button>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="faq-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg text-cfc-primary">{editing ? "Edit FAQ" : "New FAQ"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <Label className="text-xs uppercase tracking-wider text-cfc-text-muted">English</Label>
            <div><Label>Question</Label><Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} placeholder="What ages do you accept?" data-testid="faq-question-input" /></div>
            <div><Label>Answer</Label><Textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={3} data-testid="faq-answer-input" /></div>
            <Label className="text-xs uppercase tracking-wider text-blue-600 mt-2">Spanish (Espanol)</Label>
            <div><Label>Pregunta</Label><Input value={form.question_es} onChange={(e) => setForm({ ...form, question_es: e.target.value })} placeholder="Spanish translation..." /></div>
            <div><Label>Respuesta</Label><Textarea value={form.answer_es} onChange={(e) => setForm({ ...form, answer_es: e.target.value })} rows={3} /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={save} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-faq-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
};


const SiteContentEditor = ({ initialTab = "homepage" }) => {
  const [content, setContent] = useState(null);
  const [seo, setSeo] = useState({ site_title: "", site_description: "", og_image: "", keywords: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);

  const fetchContent = async () => {
    try {
      const [contentRes, seoRes] = await Promise.all([
        axios.get(`${API}/site-content`, { withCredentials: true }),
        axios.get(`${API}/seo`, { withCredentials: true }).catch(() => null),
      ]);
      const data = contentRes.data;

      // Merge defaults so editor shows current content even if not yet stored in CMS
      // Join Page
      if (!data.join_page) data.join_page = {};
      const jpHero = data.join_page.hero || {};
      data.join_page.hero = {
        badge: jpHero.badge || "Join the club where our players make the difference!",
        title: jpHero.title || "COMMONWEALTH FC TRYOUTS",
        description: jpHero.description || "Experience top-tier training and development with Commonwealth FC. Sign up for our upcoming tryouts and become part of a community dedicated to excellence in youth soccer.",
        button_text: jpHero.button_text || "Register for Information",
        ...jpHero,
      };
      const jpForm = data.join_page.form || {};
      data.join_page.form = {
        heading: jpForm.heading || "REGISTER FOR TRYOUTS AND EVALUATIONS",
        description: jpForm.description || "Sign up now to get information on how to secure your spot at the Commonwealth FC tryouts.",
        form_title: jpForm.form_title || "GET STARTED",
        success_title: jpForm.success_title || "You're In!",
        success_message: jpForm.success_message || "Thank you for your interest in Commonwealth FC. We'll be in touch within 48 hours to schedule your evaluation.",
        ...jpForm,
      };
      if (!data.join_page.consent_text && data.join_page.consent_text !== "") {
        data.join_page.consent_text = "By submitting this form, you agree to receive text messages from Commonwealth FC regarding registration, scheduling, and program updates. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance.";
      }
      if (!data.join_page.images) data.join_page.images = {};
      const jpImg = data.join_page.images;
      if (!jpImg.bg_url) jpImg.bg_url = "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/b5edc9127c51a442369721d6cda077d9d79d6ba35c55b3b335685126bd4a8e18.png";
      if (!jpImg.slider1_url) jpImg.slider1_url = "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/b2f0b4c9b1e72b4699ba0b7950d28c1d9bf8edf25108062f47dafd67c47ef853.png";
      if (!jpImg.slider2_url) jpImg.slider2_url = "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/01d0a7f9d0fae841063bad83f32292add55537d02cff1c66c93c759fd09f58a5.png";
      if (!jpImg.slider3_url) jpImg.slider3_url = "https://static.prod-images.emergentagent.com/jobs/55952a6b-5c34-41c4-a4af-2292c177e289/images/1208aa45e84dfc188966cce37da4ea7a714d6688e1291607a69ee8372be9b17b.png";

      // Coaches Page
      if (!data.coaches_page) data.coaches_page = {};
      const cp = data.coaches_page;
      if (!cp.hero_title) cp.hero_title = "BRING YOUR TEAM HOME";
      if (!cp.hero_subtitle) cp.hero_subtitle = "You built the team. We handle the rest.";
      if (!cp.hero_description) cp.hero_description = "Commonwealth FC gives coaches the freedom to focus on what matters — developing players and winning games. We take care of everything else.";
      if (!cp.hero_button) cp.hero_button = "Start a Conversation";
      if (!cp.cta_title) cp.cta_title = "READY TO BRING YOUR TEAM?";
      if (!cp.cta_description) cp.cta_description = "Join a club that values coaches and invests in your success.";
      if (!cp.cta_button) cp.cta_button = "Get in Touch";

      // Tryouts Page
      if (!data.tryouts_page) data.tryouts_page = {};
      if (!data.tryouts_page.title) data.tryouts_page.title = "TRYOUT SCHEDULE";
      if (!data.tryouts_page.subtitle) data.tryouts_page.subtitle = "Find an upcoming tryout and join us on the field.";
      if (!data.tryouts_page.empty_message) data.tryouts_page.empty_message = "No upcoming tryouts scheduled. Check back soon!";

      // FAQ Page
      if (!data.faq_page) data.faq_page = {};
      if (!data.faq_page.title) data.faq_page.title = "FREQUENTLY ASKED QUESTIONS";
      if (!data.faq_page.subtitle) data.faq_page.subtitle = "Everything you need to know about Commonwealth FC";

      // Footer
      if (data.footer && !data.footer.copyright) {
        data.footer.copyright = "All rights reserved.";
      }

      setContent(data);
      if (seoRes?.data) setSeo(seoRes.data);
    } catch (e) {
      toast.error("Failed to load site content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (section, field, value) => {
    setContent((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const updateTopLevel = (field, value) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  const updateArrayItem = (section, index, field, value) => {
    setContent((prev) => {
      const arr = [...prev[section]];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [section]: arr };
    });
  };

  const updatePhilosophyStat = (index, field, value) => {
    setContent((prev) => {
      const stats = [...prev.philosophy.stats];
      stats[index] = { ...stats[index], [field]: value };
      return { ...prev, philosophy: { ...prev.philosophy, stats } };
    });
  };

  const updateWhyParagraph = (index, value) => {
    setContent((prev) => {
      const paragraphs = [...prev.why.paragraphs];
      paragraphs[index] = value;
      return { ...prev, why: { ...prev.why, paragraphs } };
    });
  };

  const addTeam = () => {
    setContent((prev) => ({
      ...prev,
      teams: [...prev.teams, { title: "NEW TEAM", ages: "U? - U?", description: "Description..." }],
    }));
  };

  const removeTeam = (index) => {
    setContent((prev) => ({
      ...prev,
      teams: prev.teams.filter((_, i) => i !== index),
    }));
  };

  const addFeature = () => {
    setContent((prev) => ({
      ...prev,
      features: [...prev.features, { icon: "diamond-outline", title: "NEW FEATURE", description: "Description..." }],
    }));
  };

  const removeFeature = (index) => {
    setContent((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const addStat = () => {
    setContent((prev) => ({
      ...prev,
      philosophy: { ...prev.philosophy, stats: [...prev.philosophy.stats, { stat: "0", label: "Label" }] },
    }));
  };

  const removeStat = (index) => {
    setContent((prev) => ({
      ...prev,
      philosophy: { ...prev.philosophy, stats: prev.philosophy.stats.filter((_, i) => i !== index) },
    }));
  };

  const toggleSection = (key) => {
    setContent((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.key === key ? { ...s, enabled: !s.enabled } : s
      ),
    }));
  };

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setContent((prev) => {
      const sorted = [...prev.sections].sort((a, b) => a.order - b.order);
      const oldIndex = sorted.findIndex((s) => s.key === active.id);
      const newIndex = sorted.findIndex((s) => s.key === over.id);
      const reordered = arrayMove(sorted, oldIndex, newIndex).map((s, i) => ({
        ...s,
        order: i,
      }));
      return { ...prev, sections: reordered };
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/site-content`, content, { withCredentials: true }),
        axios.put(`${API}/seo`, seo, { withCredentials: true }),
      ]);
      toast.success("Site content & SEO saved! Changes are live.");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset ALL site content to defaults? This cannot be undone.")) return;
    try {
      await axios.post(`${API}/site-content/reset`, {}, { withCredentials: true });
      toast.success("Content reset to defaults");
      fetchContent();
    } catch (e) {
      toast.error("Failed to reset");
    }
  };

  if (loading || !content) {
    return <div className="text-cfc-text-muted text-center py-12">Loading site content...</div>;
  }

  return (
    <div data-testid="site-content-editor">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">
            {activeTab === "homepage" ? "Homepage" : activeTab === "pages" ? "Pages" : "Site Settings"}
          </h1>
          <p className="text-cfc-text-muted text-sm mt-1">
            {activeTab === "homepage" ? "Edit homepage branding, sections, and layout" : activeTab === "pages" ? "Edit content on Join, Coaches, and Live Streams pages" : "SEO, footer, quick links, sponsors, and FAQs"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} className="text-cfc-destructive border-cfc-destructive/30" data-testid="reset-content-btn">
            <RotateCcw size={16} className="mr-2" /> Reset Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-content-btn">
            <Save size={16} className="mr-2" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 bg-white p-1 rounded-lg border border-cfc-border" data-testid="site-content-tabs">
        {[
          { id: "homepage", label: "Homepage" },
          { id: "pages", label: "Pages" },
          { id: "settings", label: "Site Settings" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab.id
                ? "bg-cfc-primary text-white"
                : "text-cfc-text-muted hover:text-cfc-primary hover:bg-cfc-cream"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {/* ============= HOMEPAGE TAB ============= */}
        {activeTab === "homepage" && (<>
        {/* BRANDING / LOGO */}
        <Section title="Branding & Logo" icon={Shield} defaultOpen={true}>
          <ImageUploadField label="Club Logo" value={content.logo_url || ""} onChange={(url) => updateTopLevel("logo_url", url)} />
          <ImageFitSelect value={content.logo_fit} onChange={(v) => updateTopLevel("logo_fit", v)} testId="logo-image-fit-select" />
          <p className="text-xs text-cfc-text-muted">Used in navigation bar, footer, and news pages. Recommended: square or shield-shaped, transparent PNG.</p>
        </Section>

        {/* SECTION LAYOUT */}
        <Section title="Section Layout & Visibility" icon={Layers} defaultOpen={false}>
          <p className="text-xs text-cfc-text-muted mb-3">Drag to reorder, toggle to show/hide. Navigation and Footer are always shown.</p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleSectionDragEnd}
          >
            <SortableContext
              items={(content.sections || []).sort((a, b) => a.order - b.order).map((s) => s.key)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {[...(content.sections || [])].sort((a, b) => a.order - b.order).map((s) => (
                  <SortableSectionItem key={s.key} section={s} onToggle={toggleSection} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </Section>

        {/* HERO */}
        <Section title="Hero Section" icon={Image} defaultOpen={true}>
          <FieldGroup label="Title">
            <Input value={content.hero.title} onChange={(e) => updateField("hero", "title", e.target.value)} data-testid="hero-title-input" />
          </FieldGroup>
          <FieldGroup label="Tagline">
            <Input value={content.hero.tagline} onChange={(e) => updateField("hero", "tagline", e.target.value)} data-testid="hero-tagline-input" />
          </FieldGroup>
          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Primary Button Text">
              <Input value={content.hero.button_primary} onChange={(e) => updateField("hero", "button_primary", e.target.value)} />
            </FieldGroup>
            <FieldGroup label="Secondary Button Text">
              <Input value={content.hero.button_secondary} onChange={(e) => updateField("hero", "button_secondary", e.target.value)} />
            </FieldGroup>
          </div>
          <ImageUploadField label="Hero Image" value={content.hero.image_url} onChange={(url) => updateField("hero", "image_url", url)} />
          <ImageFitSelect value={content.hero.image_fit} onChange={(v) => updateField("hero", "image_fit", v)} testId="hero-image-fit-select" />
        </Section>

        {/* FEATURES */}
        <Section title="Features Section" icon={LayoutGrid}>
          {content.features.map((feature, i) => (
            <Card key={`feature-${i}-${feature.title}`} className="border-cfc-border bg-cfc-cream-alt">
              <CardContent className="p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-cfc-primary">Feature {i + 1}</span>
                  {content.features.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeFeature(i)} className="text-cfc-destructive text-xs h-7">Remove</Button>
                  )}
                </div>
                <Input value={feature.title} onChange={(e) => updateArrayItem("features", i, "title", e.target.value)} placeholder="Feature title" />
                <Textarea value={feature.description} onChange={(e) => updateArrayItem("features", i, "description", e.target.value)} placeholder="Description" rows={2} />
              </CardContent>
            </Card>
          ))}
          <Button variant="outline" size="sm" onClick={addFeature}>+ Add Feature</Button>
        </Section>

        {/* WHY SECTION */}
        <Section title="Why Commonwealth Section" icon={Target}>
          <FieldGroup label="Heading">
            <Input value={content.why.heading} onChange={(e) => updateField("why", "heading", e.target.value)} />
          </FieldGroup>
          {content.why.paragraphs.map((p, i) => (
            <FieldGroup key={`why-para-${i}`} label={`Paragraph ${i + 1}`}>
              <Textarea value={p} onChange={(e) => updateWhyParagraph(i, e.target.value)} rows={3} />
            </FieldGroup>
          ))}
          <FieldGroup label="Button Text">
            <Input value={content.why.button_text} onChange={(e) => updateField("why", "button_text", e.target.value)} />
          </FieldGroup>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <ImageUploadField label="Image 1" value={content.why.image1_url} onChange={(url) => updateField("why", "image1_url", url)} />
              <div className="mt-2">
                <ImageFitSelect value={content.why.image1_fit} onChange={(v) => updateField("why", "image1_fit", v)} testId="why-image1-fit-select" />
              </div>
            </div>
            <div>
              <ImageUploadField label="Image 2" value={content.why.image2_url} onChange={(url) => updateField("why", "image2_url", url)} />
              <div className="mt-2">
                <ImageFitSelect value={content.why.image2_fit} onChange={(v) => updateField("why", "image2_fit", v)} testId="why-image2-fit-select" />
              </div>
            </div>
          </div>
        </Section>

        {/* TEAMS */}
        <Section title="Teams Section" icon={Trophy}>
          <FieldGroup label="Section Heading">
            <Input value={content.teams_heading} onChange={(e) => updateTopLevel("teams_heading", e.target.value)} />
          </FieldGroup>
          <FieldGroup label="Section Subtext">
            <Textarea value={content.teams_subtext} onChange={(e) => updateTopLevel("teams_subtext", e.target.value)} rows={2} />
          </FieldGroup>
          {content.teams.map((team, i) => (
            <Card key={`team-${i}-${team.title}`} className="border-cfc-border bg-cfc-cream-alt">
              <CardContent className="p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-cfc-primary">Team {i + 1}</span>
                  {content.teams.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeTeam(i)} className="text-cfc-destructive text-xs h-7">Remove</Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={team.title} onChange={(e) => updateArrayItem("teams", i, "title", e.target.value)} placeholder="Team name" />
                  <Input value={team.ages} onChange={(e) => updateArrayItem("teams", i, "ages", e.target.value)} placeholder="Age range" />
                </div>
                <Textarea value={team.description} onChange={(e) => updateArrayItem("teams", i, "description", e.target.value)} placeholder="Description" rows={2} />
              </CardContent>
            </Card>
          ))}
          <Button variant="outline" size="sm" onClick={addTeam}>+ Add Team</Button>
        </Section>

        {/* PHILOSOPHY */}
        <Section title="Philosophy / Stats Section" icon={Target}>
          <FieldGroup label="Heading">
            <Input value={content.philosophy.heading} onChange={(e) => updateField("philosophy", "heading", e.target.value)} />
          </FieldGroup>
          <FieldGroup label="Body Text">
            <Textarea value={content.philosophy.body} onChange={(e) => updateField("philosophy", "body", e.target.value)} rows={4} />
          </FieldGroup>
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider">Stats</Label>
          <div className="grid grid-cols-3 gap-3">
            {content.philosophy.stats.map((s, i) => (
              <Card key={`stat-${i}-${s.label}`} className="border-cfc-border bg-cfc-cream-alt">
                <CardContent className="p-2 space-y-1">
                  <Input value={s.stat} onChange={(e) => updatePhilosophyStat(i, "stat", e.target.value)} placeholder="100%" className="text-center font-bold" />
                  <Input value={s.label} onChange={(e) => updatePhilosophyStat(i, "label", e.target.value)} placeholder="Label" className="text-center text-sm" />
                  {content.philosophy.stats.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeStat(i)} className="text-cfc-destructive text-xs h-6 w-full">Remove</Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addStat}>+ Add Stat</Button>
        </Section>

        {/* CTA */}
        <Section title="Call to Action Section" icon={MessageSquare}>
          <FieldGroup label="Subtext">
            <Input value={content.cta.subtext} onChange={(e) => updateField("cta", "subtext", e.target.value)} />
          </FieldGroup>
          <FieldGroup label="Heading">
            <Input value={content.cta.heading} onChange={(e) => updateField("cta", "heading", e.target.value)} />
          </FieldGroup>
          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Primary Button">
              <Input value={content.cta.button_primary} onChange={(e) => updateField("cta", "button_primary", e.target.value)} />
            </FieldGroup>
            <FieldGroup label="Secondary Button">
              <Input value={content.cta.button_secondary} onChange={(e) => updateField("cta", "button_secondary", e.target.value)} />
            </FieldGroup>
          </div>
        </Section>

        </>)}
        {/* ============= PAGES TAB ============= */}
        {activeTab === "pages" && (<>
        {/* COACHES PAGE */}
        <Section title="Coaches Page" icon={Shield}>
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold">Hero Section</Label>
          <FieldGroup label="Hero Title">
            <Input value={content.coaches_page?.hero_title || ""} onChange={(e) => updateField("coaches_page", "hero_title", e.target.value)} data-testid="coaches-hero-title-input" />
          </FieldGroup>
          <FieldGroup label="Hero Subtitle">
            <Input value={content.coaches_page?.hero_subtitle || ""} onChange={(e) => updateField("coaches_page", "hero_subtitle", e.target.value)} />
          </FieldGroup>
          <FieldGroup label="Hero Description">
            <Textarea value={content.coaches_page?.hero_description || ""} onChange={(e) => updateField("coaches_page", "hero_description", e.target.value)} rows={3} />
          </FieldGroup>
          <FieldGroup label="Hero Button Text">
            <Input value={content.coaches_page?.hero_button || ""} onChange={(e) => updateField("coaches_page", "hero_button", e.target.value)} />
          </FieldGroup>
          <ImageUploadField label="Hero Background Image" value={content.coaches_page?.hero_image || ""} onChange={(url) => updateField("coaches_page", "hero_image", url)} />

          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider mt-4">Benefits (displayed as cards)</Label>
          {(content.coaches_page?.benefits || []).map((benefit, index) => (
            <div key={`benefit-${index}-${benefit.title}`} className="border border-cfc-border rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold">Benefit {index + 1}</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500 text-xs h-6"
                  onClick={() => {
                    const updated = [...(content.coaches_page?.benefits || [])];
                    updated.splice(index, 1);
                    setContent(prev => ({ ...prev, coaches_page: { ...prev.coaches_page, benefits: updated } }));
                  }}
                >
                  Remove
                </Button>
              </div>
              <Input
                value={benefit.title}
                placeholder="Title"
                onChange={(e) => {
                  const updated = [...(content.coaches_page?.benefits || [])];
                  updated[index] = { ...updated[index], title: e.target.value };
                  setContent(prev => ({ ...prev, coaches_page: { ...prev.coaches_page, benefits: updated } }));
                }}
              />
              <Textarea
                value={benefit.description}
                placeholder="Description"
                rows={2}
                onChange={(e) => {
                  const updated = [...(content.coaches_page?.benefits || [])];
                  updated[index] = { ...updated[index], description: e.target.value };
                  setContent(prev => ({ ...prev, coaches_page: { ...prev.coaches_page, benefits: updated } }));
                }}
              />
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => {
              const updated = [...(content.coaches_page?.benefits || []), { title: "New Benefit", description: "Description..." }];
              setContent(prev => ({ ...prev, coaches_page: { ...prev.coaches_page, benefits: updated } }));
            }}
          >
            + Add Benefit
          </Button>
          <FieldGroup label="CTA Heading">
            <Input value={content.coaches_page?.cta_heading || ""} onChange={(e) => updateField("coaches_page", "cta_heading", e.target.value)} />
          </FieldGroup>
          <FieldGroup label="CTA Description">
            <Textarea value={content.coaches_page?.cta_description || ""} onChange={(e) => updateField("coaches_page", "cta_description", e.target.value)} rows={2} />
          </FieldGroup>
          <FieldGroup label="CTA Button Text">
            <Input value={content.coaches_page?.cta_button || ""} onChange={(e) => updateField("coaches_page", "cta_button", e.target.value)} />
          </FieldGroup>
          <ImageUploadField label="CTA Background Image" value={content.coaches_page?.cta_image || ""} onChange={(url) => updateField("coaches_page", "cta_image", url)} />
        </Section>

        {/* REEPLAYER / LIVE STREAMS */}
        <Section title="Live Streams (Reeplayer)" icon={Video}>
          <FieldGroup label="Page Title">
            <Input value={content.reeplayer?.title || "LIVE STREAMS"} onChange={(e) => updateField("reeplayer", "title", e.target.value)} data-testid="reeplayer-title-input" />
          </FieldGroup>
          <FieldGroup label="Subtitle">
            <Input value={content.reeplayer?.subtitle || ""} onChange={(e) => updateField("reeplayer", "subtitle", e.target.value)} />
          </FieldGroup>
          <FieldGroup label="Description">
            <Textarea value={content.reeplayer?.description || ""} onChange={(e) => updateField("reeplayer", "description", e.target.value)} rows={2} />
          </FieldGroup>
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider mt-4">Stream Links</Label>
          {(content.reeplayer?.reeplayer_links || []).map((link, index) => (
            <div key={`rp-${index}-${link.id}`} className="border border-cfc-border rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold">Link {index + 1}</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500 text-xs h-6"
                  onClick={() => {
                    const updated = [...(content.reeplayer?.reeplayer_links || [])];
                    updated.splice(index, 1);
                    setContent(prev => ({ ...prev, reeplayer: { ...prev.reeplayer, reeplayer_links: updated } }));
                  }}
                >
                  <Trash2 size={12} className="mr-1" /> Remove
                </Button>
              </div>
              <Input
                value={link.label}
                placeholder="Link label (e.g. U14 Boys)"
                onChange={(e) => {
                  const updated = [...(content.reeplayer?.reeplayer_links || [])];
                  updated[index] = { ...updated[index], label: e.target.value };
                  setContent(prev => ({ ...prev, reeplayer: { ...prev.reeplayer, reeplayer_links: updated } }));
                }}
              />
              <Input
                value={link.url}
                placeholder="Reeplayer link URL"
                onChange={(e) => {
                  const updated = [...(content.reeplayer?.reeplayer_links || [])];
                  updated[index] = { ...updated[index], url: e.target.value };
                  setContent(prev => ({ ...prev, reeplayer: { ...prev.reeplayer, reeplayer_links: updated } }));
                }}
              />
              <Input
                value={link.description || ""}
                placeholder="Short description (optional)"
                onChange={(e) => {
                  const updated = [...(content.reeplayer?.reeplayer_links || [])];
                  updated[index] = { ...updated[index], description: e.target.value };
                  setContent(prev => ({ ...prev, reeplayer: { ...prev.reeplayer, reeplayer_links: updated } }));
                }}
              />
              <Input
                value={link.team || ""}
                placeholder="TeamSnap team name (matches this stream to a team on the Parent Hub)"
                onChange={(e) => {
                  const updated = [...(content.reeplayer?.reeplayer_links || [])];
                  updated[index] = { ...updated[index], team: e.target.value };
                  setContent(prev => ({ ...prev, reeplayer: { ...prev.reeplayer, reeplayer_links: updated } }));
                }}
                data-testid={`reeplayer-team-input-${index}`}
              />
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            data-testid="add-reeplayer-link-btn"
            onClick={() => {
              const updated = [...(content.reeplayer?.reeplayer_links || []), { id: `rp_${Date.now()}`, label: "", url: "", description: "" }];
              setContent(prev => ({ ...prev, reeplayer: { ...prev.reeplayer, reeplayer_links: updated } }));
            }}
          >
            <Plus size={14} className="mr-1" /> Add Stream Link
          </Button>
        </Section>

        {/* TRYOUTS PAGE */}
        <Section title="Tryouts Page" icon={CalendarDays}>
          <FieldGroup label="Page Title">
            <Input value={content.tryouts_page?.title || "TRYOUT SCHEDULE"} onChange={(e) => setContent(prev => ({ ...prev, tryouts_page: { ...prev.tryouts_page, title: e.target.value } }))} />
          </FieldGroup>
          <FieldGroup label="Subtitle">
            <Textarea value={content.tryouts_page?.subtitle || ""} onChange={(e) => setContent(prev => ({ ...prev, tryouts_page: { ...prev.tryouts_page, subtitle: e.target.value } }))} rows={2} />
          </FieldGroup>
          <FieldGroup label="Empty State Message (when no tryouts)">
            <Textarea value={content.tryouts_page?.empty_message || ""} onChange={(e) => setContent(prev => ({ ...prev, tryouts_page: { ...prev.tryouts_page, empty_message: e.target.value } }))} rows={2} />
          </FieldGroup>
          <ImageUploadField label="Hero Background Image" value={content.tryouts_page?.hero_image || ""} onChange={(url) => setContent(prev => ({ ...prev, tryouts_page: { ...prev.tryouts_page, hero_image: url } }))} />
        </Section>

        {/* FAQ PAGE */}
        <Section title="FAQ Page" icon={HelpCircle}>
          <FieldGroup label="Page Title">
            <Input value={content.faq_page?.title || "FREQUENTLY ASKED QUESTIONS"} onChange={(e) => setContent(prev => ({ ...prev, faq_page: { ...prev.faq_page, title: e.target.value } }))} />
          </FieldGroup>
          <FieldGroup label="Subtitle">
            <Textarea value={content.faq_page?.subtitle || ""} onChange={(e) => setContent(prev => ({ ...prev, faq_page: { ...prev.faq_page, subtitle: e.target.value } }))} rows={2} />
          </FieldGroup>
          <p className="text-xs text-cfc-text-muted">FAQ items are managed in the Settings tab under FAQ Manager.</p>
        </Section>

        {/* JOIN PAGE CONTENT */}
        <Section title="Join / Tryouts Page" icon={ClipboardList}>
          <p className="text-xs text-cfc-text-muted mb-4">Edit all content on the /join registration page. Changes apply immediately when saved.</p>
          
          {/* Hero */}
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold">Hero Section</Label>
          <FieldGroup label="Badge Text (above title)">
            <Input value={content.join_page?.hero?.badge || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, hero: { ...prev.join_page?.hero, badge: e.target.value } } }))} data-testid="join-hero-badge-input" />
          </FieldGroup>
          <FieldGroup label="Title">
            <Input value={content.join_page?.hero?.title || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, hero: { ...prev.join_page?.hero, title: e.target.value } } }))} data-testid="join-hero-title-input" />
          </FieldGroup>
          <FieldGroup label="Description">
            <Textarea value={content.join_page?.hero?.description || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, hero: { ...prev.join_page?.hero, description: e.target.value } } }))} rows={3} />
          </FieldGroup>
          <FieldGroup label="Button Text">
            <Input value={content.join_page?.hero?.button_text || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, hero: { ...prev.join_page?.hero, button_text: e.target.value } } }))} />
          </FieldGroup>

          {/* Images */}
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold mt-6">Page Images</Label>
          <ImageUploadField label="Background Image (behind hero)" value={content.join_page?.images?.bg_url || ""} onChange={(url) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, images: { ...prev.join_page?.images, bg_url: url } } }))} />
          <ImageUploadField label="Slider Image 1 (match action)" value={content.join_page?.images?.slider1_url || ""} onChange={(url) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, images: { ...prev.join_page?.images, slider1_url: url } } }))} />
          <ImageUploadField label="Slider Image 2 (training/portrait)" value={content.join_page?.images?.slider2_url || ""} onChange={(url) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, images: { ...prev.join_page?.images, slider2_url: url } } }))} />
          <ImageUploadField label="Slider Image 3 (celebration/team)" value={content.join_page?.images?.slider3_url || ""} onChange={(url) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, images: { ...prev.join_page?.images, slider3_url: url } } }))} />

          {/* Features */}
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold mt-6">Feature Cards (3 cards shown below hero)</Label>
          {(content.join_page?.features || []).map((feature, idx) => (
            <div key={`jf-${idx}`} className="border border-cfc-border rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold">Feature {idx + 1}</Label>
                <Button size="sm" variant="ghost" className="text-red-500 text-xs h-6" onClick={() => {
                  const updated = [...(content.join_page?.features || [])];
                  updated.splice(idx, 1);
                  setContent(prev => ({ ...prev, join_page: { ...prev.join_page, features: updated } }));
                }}>
                  <Trash2 size={12} className="mr-1" /> Remove
                </Button>
              </div>
              <Input value={feature.title} placeholder="Feature title" onChange={(e) => {
                const updated = [...(content.join_page?.features || [])];
                updated[idx] = { ...updated[idx], title: e.target.value };
                setContent(prev => ({ ...prev, join_page: { ...prev.join_page, features: updated } }));
              }} />
              <Textarea value={feature.description} placeholder="Feature description" rows={2} onChange={(e) => {
                const updated = [...(content.join_page?.features || [])];
                updated[idx] = { ...updated[idx], description: e.target.value };
                setContent(prev => ({ ...prev, join_page: { ...prev.join_page, features: updated } }));
              }} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-2" onClick={() => {
            const updated = [...(content.join_page?.features || []), { title: "", description: "" }];
            setContent(prev => ({ ...prev, join_page: { ...prev.join_page, features: updated } }));
          }}>
            <Plus size={14} className="mr-1" /> Add Feature
          </Button>

          {/* Form Section */}
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold mt-6">Registration Form Section</Label>
          <FieldGroup label="Section Heading">
            <Input value={content.join_page?.form?.heading || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, form: { ...prev.join_page?.form, heading: e.target.value } } }))} />
          </FieldGroup>
          <FieldGroup label="Section Description">
            <Textarea value={content.join_page?.form?.description || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, form: { ...prev.join_page?.form, description: e.target.value } } }))} rows={2} />
          </FieldGroup>
          <FieldGroup label="Form Title (inside form card)">
            <Input value={content.join_page?.form?.form_title || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, form: { ...prev.join_page?.form, form_title: e.target.value } } }))} />
          </FieldGroup>
          <FieldGroup label="Success Title (after submit)">
            <Input value={content.join_page?.form?.success_title || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, form: { ...prev.join_page?.form, success_title: e.target.value } } }))} />
          </FieldGroup>
          <FieldGroup label="Success Message">
            <Textarea value={content.join_page?.form?.success_message || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, form: { ...prev.join_page?.form, success_message: e.target.value } } }))} rows={2} />
          </FieldGroup>
          <FieldGroup label="SMS Consent Text (small print below form)">
            <Textarea value={content.join_page?.consent_text || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, consent_text: e.target.value } }))} rows={2} placeholder="By submitting, you consent to receiving..." />
          </FieldGroup>

          {/* Age Groups Table */}
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold mt-6">Age Groups Table</Label>
          {(content.join_page?.age_groups || []).map((group, idx) => (
            <div key={`ag-${idx}`} className="border border-cfc-border rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold">Group {idx + 1}</Label>
                <Button size="sm" variant="ghost" className="text-red-500 text-xs h-6" onClick={() => {
                  const updated = [...(content.join_page?.age_groups || [])];
                  updated.splice(idx, 1);
                  setContent(prev => ({ ...prev, join_page: { ...prev.join_page, age_groups: updated } }));
                }}>
                  <Trash2 size={12} className="mr-1" /> Remove
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input value={group.age} placeholder="Age Group (e.g. U12-U14)" onChange={(e) => {
                  const updated = [...(content.join_page?.age_groups || [])];
                  updated[idx] = { ...updated[idx], age: e.target.value };
                  setContent(prev => ({ ...prev, join_page: { ...prev.join_page, age_groups: updated } }));
                }} />
                <Input value={group.birth} placeholder="Birth Years (e.g. 2014-2012)" onChange={(e) => {
                  const updated = [...(content.join_page?.age_groups || [])];
                  updated[idx] = { ...updated[idx], birth: e.target.value };
                  setContent(prev => ({ ...prev, join_page: { ...prev.join_page, age_groups: updated } }));
                }} />
                <Input value={group.status} placeholder="Status (Open / Limited / etc)" onChange={(e) => {
                  const updated = [...(content.join_page?.age_groups || [])];
                  updated[idx] = { ...updated[idx], status: e.target.value };
                  setContent(prev => ({ ...prev, join_page: { ...prev.join_page, age_groups: updated } }));
                }} />
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-2" onClick={() => {
            const updated = [...(content.join_page?.age_groups || []), { age: "", birth: "", status: "Open" }];
            setContent(prev => ({ ...prev, join_page: { ...prev.join_page, age_groups: updated } }));
          }}>
            <Plus size={14} className="mr-1" /> Add Age Group
          </Button>

          {/* FAQs */}
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold mt-6">Tryout FAQs</Label>
          {(content.join_page?.faqs || []).map((faq, idx) => (
            <div key={`jfaq-${idx}`} className="border border-cfc-border rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold">FAQ {idx + 1}</Label>
                <Button size="sm" variant="ghost" className="text-red-500 text-xs h-6" onClick={() => {
                  const updated = [...(content.join_page?.faqs || [])];
                  updated.splice(idx, 1);
                  setContent(prev => ({ ...prev, join_page: { ...prev.join_page, faqs: updated } }));
                }}>
                  <Trash2 size={12} className="mr-1" /> Remove
                </Button>
              </div>
              <Input value={faq.question} placeholder="Question" onChange={(e) => {
                const updated = [...(content.join_page?.faqs || [])];
                updated[idx] = { ...updated[idx], question: e.target.value };
                setContent(prev => ({ ...prev, join_page: { ...prev.join_page, faqs: updated } }));
              }} />
              <Textarea value={faq.answer} placeholder="Answer" rows={2} onChange={(e) => {
                const updated = [...(content.join_page?.faqs || [])];
                updated[idx] = { ...updated[idx], answer: e.target.value };
                setContent(prev => ({ ...prev, join_page: { ...prev.join_page, faqs: updated } }));
              }} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-2" onClick={() => {
            const updated = [...(content.join_page?.faqs || []), { question: "", answer: "" }];
            setContent(prev => ({ ...prev, join_page: { ...prev.join_page, faqs: updated } }));
          }}>
            <Plus size={14} className="mr-1" /> Add FAQ
          </Button>

          {/* CTA */}
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold mt-6">Bottom CTA Section</Label>
          <FieldGroup label="CTA Title">
            <Input value={content.join_page?.cta?.title || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, cta: { ...prev.join_page?.cta, title: e.target.value } } }))} />
          </FieldGroup>
          <FieldGroup label="CTA Subtitle">
            <Input value={content.join_page?.cta?.subtitle || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, cta: { ...prev.join_page?.cta, subtitle: e.target.value } } }))} />
          </FieldGroup>
          <FieldGroup label="CTA Badge">
            <Input value={content.join_page?.cta?.badge || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, cta: { ...prev.join_page?.cta, badge: e.target.value } } }))} />
          </FieldGroup>
          <FieldGroup label="CTA Button Text">
            <Input value={content.join_page?.cta?.button_text || ""} onChange={(e) => setContent(prev => ({ ...prev, join_page: { ...prev.join_page, cta: { ...prev.join_page?.cta, button_text: e.target.value } } }))} />
          </FieldGroup>

          {/* Age Options for dropdown */}
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider font-bold mt-6">Form Age Dropdown Options</Label>
          <p className="text-xs text-cfc-text-muted mb-2">One per line. These appear in the registration form dropdown.</p>
          <Textarea
            value={(content.join_page?.age_options || []).join("\n")}
            onChange={(e) => {
              const options = e.target.value.split("\n").filter(o => o.trim());
              setContent(prev => ({ ...prev, join_page: { ...prev.join_page, age_options: options } }));
            }}
            rows={4}
            placeholder="U8 (2018-2019)&#10;U10 (2016-2017)&#10;U12 (2014-2015)"
            data-testid="join-age-options-textarea"
          />
        </Section>

        </>)}
        {/* ============= SETTINGS TAB ============= */}
        {activeTab === "settings" && (<>
        {/* SEO */}
        <Section title="SEO & Social Sharing" icon={Search} defaultOpen={true}>
          <FieldGroup label="Page Title (shows in browser tab & Google)">
            <Input value={seo.site_title} onChange={(e) => setSeo({ ...seo, site_title: e.target.value })} placeholder="Commonwealth FC | Youth Soccer Club" data-testid="seo-title-input" />
          </FieldGroup>
          <FieldGroup label="Meta Description (shows in Google results)">
            <Textarea value={seo.site_description} onChange={(e) => setSeo({ ...seo, site_description: e.target.value })} rows={2} placeholder="Commonwealth FC is a premier youth soccer club..." data-testid="seo-description-input" />
          </FieldGroup>
          <FieldGroup label="Keywords (comma-separated)">
            <Input value={seo.keywords} onChange={(e) => setSeo({ ...seo, keywords: e.target.value })} placeholder="youth soccer, Commonwealth FC, tryouts" />
          </FieldGroup>
          <ImageUploadField label="Social Share Image (OG Image)" value={seo.og_image || ""} onChange={(url) => setSeo({ ...seo, og_image: url })} />
          <p className="text-xs text-cfc-text-muted">This image appears when someone shares your site on Facebook, Twitter, etc.</p>
        </Section>

        {/* FOOTER */}
        <Section title="Footer / Contact Info" icon={MapPin}>
          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Email">
              <Input value={content.footer.email} onChange={(e) => updateField("footer", "email", e.target.value)} data-testid="footer-email-input" />
            </FieldGroup>
            <FieldGroup label="Phone">
              <Input value={content.footer.phone} onChange={(e) => updateField("footer", "phone", e.target.value)} />
            </FieldGroup>
          </div>
          <FieldGroup label="Location">
            <Input value={content.footer.location} onChange={(e) => updateField("footer", "location", e.target.value)} />
          </FieldGroup>
          <FieldGroup label="Copyright Text">
            <Input value={content.footer.copyright || ""} onChange={(e) => updateField("footer", "copyright", e.target.value)} placeholder="e.g., © 2026 Commonwealth FC. All rights reserved." />
          </FieldGroup>
          <ImageUploadField label="Footer Logo (overrides header logo)" value={content.footer.logo_url || ""} onChange={(url) => updateField("footer", "logo_url", url)} />
          <Label className="text-xs text-cfc-text-muted uppercase tracking-wider">Social Links (URL)</Label>
          <div className="grid grid-cols-3 gap-3">
            <FieldGroup label="Instagram">
              <Input value={content.footer.instagram} onChange={(e) => updateField("footer", "instagram", e.target.value)} placeholder="https://instagram.com/..." />
            </FieldGroup>
            <FieldGroup label="Twitter / X">
              <Input value={content.footer.twitter} onChange={(e) => updateField("footer", "twitter", e.target.value)} placeholder="https://twitter.com/..." />
            </FieldGroup>
            <FieldGroup label="Facebook">
              <Input value={content.footer.facebook} onChange={(e) => updateField("footer", "facebook", e.target.value)} placeholder="https://facebook.com/..." />
            </FieldGroup>
          </div>
        </Section>

        {/* Self-contained managers */}
        <QuickLinksSection />
        <SponsorsSection />
        <FAQSection />
        </>)}
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-0 mt-6 py-3 mb-10 bg-cfc-cream border-t border-cfc-border flex justify-end gap-2 z-10">
        <Button onClick={handleSave} disabled={saving} className="bg-cfc-primary hover:bg-cfc-primary-hover" data-testid="save-content-btn-bottom">
          <Save size={16} className="mr-2" /> {saving ? "Saving..." : "Save All Changes"}
        </Button>
      </div>
    </div>
  );
};

export default SiteContentEditor;
